import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import LoadingButton from '@mui/lab/LoadingButton'
import { Box, TextField, IconButton } from '@mui/material'
import Divider from '@mui/material/Divider'
import React, { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDropzone } from 'react-dropzone'
import { DropZoneBoxStyle } from '~/src/components/molecules/Form/SendMessageForm/DropZone.styles'
import SaveIcon from '@mui/icons-material/Save'

interface ISendMessageFormProps {
  message: string
  filesToUpload: File[]
  isLoading: boolean
  isPosting: boolean
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  handleChangeMessage: (event: React.ChangeEvent<HTMLInputElement>) => void
  uploadFile: (e: React.ChangeEvent<HTMLInputElement>) => void
  setFilesToUpload: (files: File[]) => void
}

/**
 * SendMessageForm
 * message form
 * @param ISendMessageFormProps
 * @returns JSX.Element
 */
export const SendMessageForm: React.FC<ISendMessageFormProps> = ({
  message,
  filesToUpload,
  isLoading,
  isPosting,
  handleSubmit,
  handleChangeMessage,
  uploadFile,
  setFilesToUpload,
  children,
}): JSX.Element => {
  const { t } = useTranslation()
  const inputFile = useRef<any>(null)

  const onDrop = useCallback(
    (acceptedFiles) => {
      // Do something with the files
      setFilesToUpload([...filesToUpload, ...acceptedFiles])
    },
    [filesToUpload]
  )
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

  /**
   * handleUploadBtnClick
   */
  const handleUploadBtnClick = () => {
    inputFile.current?.click()
  }

  return (
    <form onSubmit={handleSubmit}>
      <Box borderBottom={'1px solid #EEEEEE'} p={1} />
      <TextField
        sx={{
          '& .MuiInputBase-root': {
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            padding: '8px 16px',
            fontSize: 16,
            lineHeight: '24px',
          },
          width: '100%',
        }}
        placeholder={t('contact_order_detail.placeholder_message')}
        id="standard-multiline-static"
        rows={5}
        variant={'standard'}
        multiline
        value={message}
        onChange={handleChangeMessage}
      />
      <Box display={'flex'} sx={{ padding: 1 }}>
        <Box
          display={'flex'}
          alignItems={'flex-start'}
          width={'100%'}
          minHeight={isDragActive ? '200px' : 'auto'}
          sx={{
            position: 'relative',
            background: isDragActive ? '#F7F7F7' : '#FFFFFF',
          }}
          {...getRootProps()}
        >
          <input
            ref={inputFile}
            multiple
            accept="image/png, image/gif, image/jpeg"
            id={'file'}
            style={{ display: 'none' }}
            type={'file'}
            onChange={uploadFile}
            {...getInputProps()}
          />
          {isDragActive && (
            <Box sx={DropZoneBoxStyle} position={'absolute'} left={'12px'} top={'8px'}>
              Drop Your Files Here
            </Box>
          )}
          <Box position={'absolute'} right={0}>
            <IconButton onClick={handleUploadBtnClick}>
              <ImageOutlinedIcon />
            </IconButton>
          </Box>
        </Box>

        <Divider
          sx={{
            margin: '5px 10px',
            background: '#C4C4C4',
            width: '1px',
            height: '32px',
          }}
          orientation="vertical"
          flexItem
          variant={'middle'}
        />
        <LoadingButton
          color="primary"
          type="submit"
          loading={isLoading || isPosting}
          loadingPosition="start"
          startIcon={<SaveIcon />}
          variant="contained"
          size={'medium'}
          sx={{
            minWidth: isLoading || isPosting ? '131px' : '86px',
            height: '42px',
            fontSize: '15px',
            color: 'white',
            background: '#3F51B5',
          }}
          disabled={!filesToUpload.length && !message}
        >
          {t('contact_order_detail.send')}
        </LoadingButton>
      </Box>
    </form>
  )
}
